<template>
  <div class="mirrors-groups-tab">
    <div class="mirrors-groups-tab__content">

    <v-sheet outlined rounded class="d-flex align-center pa-3 mt-2">
      <v-icon class="mr-4" color="primary">mdi-server-network</v-icon>

      <div class="d-flex align-start">
        <div class="mr-6">
          <div class="caption text-uppercase grey--text">Main Domain</div>
          <div v-if="!loading && mainDomainData" class="body-2 font-weight-medium">{{ mainDomainData.name }}</div>
        </div>
        <div>
          <div class="caption text-uppercase grey--text">NS Status</div>
          <v-chip
            v-if="!loading && mainDomainData"
            small
            outlined
            :color="mainDomainData.status === 'active' ? 'success' : 'grey'"
            class="pa-2 text-capitalize"
          >
            {{ mainDomainData.status }}
          </v-chip>
        </div>
      </div>

      <v-spacer />

      <template v-if="canEdit && selectedMirrorIds.length">
        <v-btn
          outlined
          small
          color="warning"
          class="mr-4 px-2"
          :loading="disableMirrorLoading"
          :disabled="disableMirrorLoading || delMirrorLoading"
          @click="onDisableSelected"
        >
          <v-icon class="mr-1" small>mdi-close-box-outline</v-icon>
          DISABLE
        </v-btn>
        <v-btn
          outlined
          small
          color="error"
          class="mr-4 px-2"
          :loading="delMirrorLoading"
          :disabled="disableMirrorLoading || delMirrorLoading"
          @click="onDeleteSelected"
        >
          <v-icon class="mr-1" small>mdi-delete-outline</v-icon>
          DELETE
        </v-btn>
      </template>

      <div class="d-flex align-center" style="gap: 12px">
        <v-btn
          v-if="canReadFeatureConfig"
          outlined
          small
          :color="hasProject ? 'primary' : 'grey lighten-1'"
          class="px-2"
          :disabled="!hasProject"
          @click="goToFeatureConfig"
        >
          Feature Config
        </v-btn>
        <v-btn
          v-if="canEdit"
          small
          color="success"
          class="px-2"
          @click="openGroupForm(null)"
        >
          Add new group
        </v-btn>
      </div>
    </v-sheet>

    <v-expansion-panels
      v-if="hasProject && !loading && sortedMirrorGroups.length"
      v-model="groupsPanel"
      multiple
      accordion
      class="mt-4 d-flex expansion"
    >
      <v-expansion-panel
        v-for="group in sortedMirrorGroups"
        :key="group.group_mirror_id"
        class="expansion-panel"
      >
        <v-expansion-panel-header class="expansion-panel-header">
          <v-row
            :align="'center'"
            class="d-flex order-2 justify-space-between ml-2 flex-nowrap"
            style="max-height: 100%;"
            no-gutters
          >
            <div class="d-flex align-center" style="gap: 48px">
              <div class="d-flex align-center">
                <span class="subtitle-2" >{{ group.name }}</span>
                <div class="expansion-panel__length ml-2">
                  {{ getFilteredGroupMirrors(group).length }}
                </div>
              </div>

              <div v-if="getPrimaryMirror(group)" class="d-flex flex-column justify-space-between expansion-panel-header__col" >
                <span class="caption grey--text text-uppercase">Primary Mirror</span>
                <div class="d-flex">
                  <v-icon small color="orange">mdi-crown</v-icon>
                  <span class="caption orange--text font-weight-medium">{{ getPrimaryMirror(group).name }}</span>
                </div>
              </div>

              <div class="d-flex flex-column justify-space-between expansion-panel-header__col">
                <span class="caption grey--text text-uppercase">Default Rule</span>
                <div class="d-flex">
                  <span class="text-capitalize caption grey--text text--darken-2">{{ getGeo(group).rule }}</span>
                  <v-icon
                    v-if="canEdit"
                    small
                    color="grey"
                    class="ml-1"
                    @click.stop="openGroupForm(group)"
                  >
                    mdi-pencil
                  </v-icon>
                </div>
              </div>

              <div
                v-if="getGeo(group).allowedDisplay.length"
                class="d-flex flex-column justify-space-between expansion-panel-header__col"
              >
                <span class="caption grey--text text-uppercase">Allowed Geo</span>
                <lang-row
                  :countries="getGeo(group).allowedDisplay"
                  type="outline"
                  color="success"
                  :max-items="3"
                  class="ml-1 auto-width"
                />
              </div>

              <div
                v-if="getGeo(group).blockedDisplay.length"
                class="d-flex flex-column justify-space-between expansion-panel-header__col"
              >
                <span class="caption grey--text text-uppercase">Blocked Geo</span>
                <lang-row
                  :countries="getGeo(group).blockedDisplay"
                  type="outline"
                  color="error"
                  :max-items="3"
                  class="ml-1 auto-width"
                />
              </div>

              <div
                class="d-flex flex-column justify-space-between expansion-panel-header__col"
              >
                <span class="caption grey--text text-uppercase">Redirector</span>
                <div class="d-flex">
                  <lang-row
                    :countries="getGeo(group).redirect"
                    type="outline"
                    color="grey"
                    :max-items="3"
                    class="ml-1 auto-width"
                  />
                  <v-icon
                    small
                    :color="groupRedirectorName(group.group_mirror_id) ? 'blue' : 'grey'"
                    class="ml-1"
                  >mdi-redo-variant</v-icon>
                  <span
                    class="caption ml-1"
                    :class="groupRedirectorName(group.group_mirror_id) ? 'blue--text' : 'grey--text'"
                  >
                    {{ groupRedirectorName(group.group_mirror_id) || 'none' }}
                  </span>
                </div>
              </div>
            </div>

              <v-spacer />
              <v-btn
                v-if="canEdit"
                small
                color="success"
                class="px-2 mr-2"
                @click.stop="openMirrorForm({ entity_id: project, group_mirror_id: group.group_mirror_id }, { readonly: false })"
              >
                Add New Mirror
              </v-btn>
              <v-btn v-if="canEdit" small outlined color="success" class="px-2" @click.stop="openGroupForm(group)">Edit Group</v-btn>
          </v-row>

        </v-expansion-panel-header>

        <v-expansion-panel-content class="expansion-panel-content">
          <v-divider></v-divider>
          <v-data-table
            :value="selectedByGroup[group.group_mirror_id] || []"
            :headers="tableHeaders"
            :items="getFilteredGroupMirrors(group)"
            item-key="mirror_id"
            hide-default-footer
            :show-select="canEdit"
            disable-pagination
            outlined
            class="expansion-panel-content__table ma-4"
            @input="updateSelected(group.group_mirror_id, $event)"
          >
            <template #body.prepend>
              <tr v-if="Object.keys(filters).length > 0" class="grey lighten-3">
                <td v-if="canEdit"></td>
                <td v-for="header in tableHeaders" :key="header.value">
                  <v-select
                    v-if="filters.hasOwnProperty(header.value) && filters[header.value].type === 'select'"
                    v-model="filters[header.value].data"
                    :items="statusFilterValues"
                    hide-details
                    dense
                    multiple
                    clearable
                    class="mt-0"
                  />
                  <v-text-field
                    v-if="filters.hasOwnProperty(header.value) && filters[header.value].type === 'text'"
                    v-model="filters[header.value].data"
                    hide-details
                    dense
                    class="mt-0"
                  />
                </td>
              </tr>
            </template>

            <template #item.mirror="{ item }">
              <div
                v-if="item.mirror_id === group.primary_mirror_id"
                class="d-flex align-center"
              >
                <v-icon small color="orange">mdi-crown</v-icon>
                <span class="orange--text font-weight-medium">{{ item.name }}</span>
              </div>
              <div v-else class="d-flex align-center">
                <span class="font-weight-medium">{{ item.name }}</span>
              </div>
            </template>

            <template #item.defaultRule="{ item }">
              <span class="text-capitalize">{{ getGeo(item).rule }}</span>
            </template>

            <template #item.redirectGeo="{ item }">
              <div
                v-if="getGeo(item).redirect.length"
                class="mirrors-groups-tab__geo-cell"
                @click.stop="openGeoListDialog({ title: 'Redirector Geo', countries: getGeo(item).redirect })"
              >
                <lang-row
                  :countries="getGeo(item).redirect"
                  type="outline"
                  color="grey"
                  :max-items="3"
                  class="lang-row auto-width mirrors-groups-tab__geo-cell-items"
                />
              </div>
            </template>

            <template #item.allowGeo="{ item }">
              <div
                v-if="getGeo(item).allowedDisplay.length"
                class="mirrors-groups-tab__geo-cell"
                @click.stop="openGeoListDialog({ title: 'Allow Geo', countries: getGeo(item).allowedDisplay })"
              >
                <lang-row
                  :countries="getGeo(item).allowedDisplay"
                  type="outline"
                  color="success"
                  :max-items="3"
                  class="lang-row auto-width mirrors-groups-tab__geo-cell-items"
                />
              </div>
            </template>

            <template #item.blockedGeo="{ item }">
              <div
                v-if="getGeo(item).blockedDisplay.length"
                class="mirrors-groups-tab__geo-cell"
                @click.stop="openGeoListDialog({ title: 'Blocked Geo', countries: getGeo(item).blockedDisplay })"
              >
                <lang-row
                  :countries="getGeo(item).blockedDisplay"
                  type="outline"
                  color="error"
                  :max-items="3"
                  class="lang-row auto-width mirrors-groups-tab__geo-cell-items"
                />
              </div>
            </template>

            <template #item.targetGeo="{ item }">
              <lang-row
                v-if="targetGeoCountries(item).length"
                :countries="targetGeoCountries(item)"
                type="outline"
                color="grey"
                :max-items="3"
                class="lang-row auto-width"
              />
            </template>

            <template #item.locales="{ item }">
              <div
                v-if="mirrorLocales(item).length"
                class="mirrors-groups-tab__geo-cell"
                @click.stop="openLocaleListDialog(item)"
              >
                <lang-row
                  :countries="mirrorLocales(item)"
                  type="outline"
                  color="success"
                  :max-items="3"
                  :fill-first="true"
                  class="lang-row auto-width mirrors-groups-tab__geo-cell-items"
                />
              </div>
            </template>

            <template #item.status="{ item }">
              <v-chip
                outlined
                :color="statusColor(item.status)"
                class="text-capitalize mirrors-groups-tab__pill-chip-lg"
              >
                {{ item.status }}
              </v-chip>
            </template>

            <template #item.comments="{ item }">
              <div
                v-if="item?.settings?.comment"
                class="mirrors-groups-tab__geo-cell d-inline-flex align-center justify-center"
                @click.stop="openCommentViewDialog(item)"
              >
                <v-icon small color="green">mdi-comment</v-icon>
              </div>
            </template>

            <template #item.features="{ item }">
              <div
                v-if="(item?.feature_config?.tags || []).length"
                class="mirrors-groups-tab__geo-cell d-flex align-center justify-center"
                style="gap: 4px"
                @click.stop="openFeaturesViewDialog(item)"
              >
                <v-chip
                  outlined
                  color="warning"
                  class="text-capitalize mirrors-groups-tab__geo-cell-items mirrors-groups-tab__pill-chip-lg"
                >
                  {{ tagLabel((item?.feature_config?.tags || [])[0]) }}
                </v-chip>
                <div
                  v-if="(item?.feature_config?.tags || []).length > 1"
                  class="expansion-panel__length"
                >
                  +{{ (item?.feature_config?.tags || []).length - 1 }}
                </div>
              </div>
            </template>

            <template #item.actions="{ item }">
              <v-menu offset-y left>
                <template #activator="{ on }">
                  <v-btn icon small v-on="on">
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list dense class="mirrors-groups-tab__actions-menu py-1">
                  <v-list-item @click="openMirrorForm(item, { readonly: true })">
                    <v-list-item-icon><v-icon small>mdi-eye-outline</v-icon></v-list-item-icon>
                    <v-list-item-content><v-list-item-title>Overview</v-list-item-title></v-list-item-content>
                  </v-list-item>

                  <v-list-item v-if="canEdit" @click="openMirrorForm(item, { readonly: false })">
                    <v-list-item-icon><v-icon small>mdi-pencil-outline</v-icon></v-list-item-icon>
                    <v-list-item-content><v-list-item-title>Edit mode</v-list-item-title></v-list-item-content>
                  </v-list-item>

                  <v-list-item @click="$emit('show-audit-log', { mirrorId: item.mirror_id })">
                    <v-list-item-icon><v-icon small>mdi-history</v-icon></v-list-item-icon>
                    <v-list-item-content><v-list-item-title>Audit Log</v-list-item-title></v-list-item-content>
                  </v-list-item>

                  <v-list-item v-if="canEdit" @click="openCommentForm(item)">
                    <v-list-item-icon><v-icon small>mdi-comment-outline</v-icon></v-list-item-icon>
                    <v-list-item-content><v-list-item-title>Add Comment</v-list-item-title></v-list-item-content>
                  </v-list-item>

                  <v-list-item v-if="canEdit" @click="openMirrorForm(item, { readonly: false, duplicate: true })">
                    <v-list-item-icon><v-icon small>mdi-content-copy</v-icon></v-list-item-icon>
                    <v-list-item-content><v-list-item-title>Duplicate Mirror</v-list-item-title></v-list-item-content>
                  </v-list-item>

                  <v-list-item v-if="canEdit" @click="openDisableDialog(item)">
                    <v-list-item-icon><v-icon small>mdi-close-box-outline</v-icon></v-list-item-icon>
                    <v-list-item-content><v-list-item-title>Disable</v-list-item-title></v-list-item-content>
                  </v-list-item>

                  <v-list-item v-if="canEdit" @click="openDeleteDialog(item)">
                    <v-list-item-icon><v-icon small>mdi-delete-outline</v-icon></v-list-item-icon>
                    <v-list-item-content><v-list-item-title>Delete</v-list-item-title></v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <div v-if="hasProject && !loading && sortedMirrorGroups.length === 0" class="pa-6 grey--text text-center caption">
      No groups configured
    </div>
    </div>

    <v-sheet
      v-if="hasProject && !loading"
      outlined
      rounded
      class="mirrors-groups-tab__footer mt-4 pa-3"
    >
      <div class="d-flex align-start">
        <span class="caption font-weight-medium mr-4">Status info</span>
        <div class="mirrors-groups-tab__footer-status-list">
          <div
            v-for="item in statusInfoItems"
            :key="item.key"
            class="mirrors-groups-tab__footer-status"
          >
            <v-chip
              outlined
              :color="statusColor(item.key)"
              class="mirrors-groups-tab__footer-status-chip mirrors-groups-tab__pill-chip"
            >
              {{ item.title }}
            </v-chip>
            <span class="caption">{{ item.description }}</span>
          </div>
        </div>
      </div>
    </v-sheet>

    <drawer-dialog v-model="groupFormOpen" width="70%">
      <mirror-group-form
        v-if="groupFormOpen"
        :group="selectedGroup"
        :redirector-name="groupRedirectorName(selectedGroup?.group_mirror_id)"
        :mirrors="allUniqueMirrors"
        :project="project"
        :project_config="project_config"
        :is-saving="groupSaveLoading"
        @save="saveGroup"
        @close="closeGroupForm"
      />
    </drawer-dialog>

    <drawer-dialog v-model="mirrorFormOpen" width="70%">
      <mirror-form
        v-if="mirrorFormOpen"
        :mirror="selectedMirror"
        :mirrors="allUniqueMirrors"
        :groups="mirror_groups"
        :redirectors="redirectors"
        :project="project"
        :project_config="project_config"
        :readonly="mirrorFormReadonly"
        :is-duplicate="mirrorFormDuplicate"
        :is-saving="mirrorSaveLoading"
        :can-read-feature-config="canReadFeatureConfig"
        @save="saveMirror"
        @close="closeMirrorForm"
      />
    </drawer-dialog>

    <drawer-dialog v-model="commentFormOpen" width="70%">
      <mirror-comment-form
        v-if="commentFormOpen"
        :mirror="commentTargetMirror"
        :is-loading="mirrorSaveLoading"
        @save="saveMirror"
        @close="closeCommentForm"
      />
    </drawer-dialog>

    <mirror-geo-list-dialog
      :value="geoListDialog"
      :title="geoListTitle"
      :countries="geoListCountries"
      :list-kind="geoListKind"
      :default-locale="geoListDefaultLocale"
      @input="onGeoListDialogInput"
    />

    <v-dialog
      :value="commentViewDialog"
      width="560"
      @input="onCommentViewDialogInput"
    >
      <v-card>
        <v-card-title class="body-1" primary-title>
          Comment
          <v-spacer />
          <v-btn icon @click="closeCommentViewDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pb-6">
          <v-textarea
            :value="commentViewBody"
            readonly
            outlined
            hide-details
            auto-grow
            rows="6"
            class="mt-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      :value="featuresViewDialog"
      width="560"
      @input="onFeaturesViewDialogInput"
    >
      <v-card>
        <v-card-title class="body-1" primary-title>
          Customized features
          <v-spacer />
          <v-btn icon @click="closeFeaturesViewDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pb-6">
          <div class="d-flex flex-wrap align-center" style="gap: 8px">
            <v-chip
              v-for="tag in featuresViewTags"
              :key="tag"
              outlined
              color="warning"
              class="text-capitalize mirrors-groups-tab__pill-chip"
            >
              {{ tagLabel(tag) }}
            </v-chip>
            <v-btn
              v-if="canReadFeatureConfig"
              icon
              small
              color="success"
              @click.stop="navigateToFeatureConfigForMirror"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deleteDialog"
      width="560"
    >
      <v-card :loading="delMirrorLoading">
        <v-card-title class="body-1" primary-title>
          Delete Mirror
          <v-spacer />
          <v-btn icon :disabled="delMirrorLoading" @click="closeDeleteDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="py-6">
          <span class="body-1 grey--text text--darken-4">
            Mirror
            <span class="black--text font-weight-medium">{{ deleteTarget?.name || '-' }}</span>
            will be deleting
          </span>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            :disabled="delMirrorLoading"
            text
            @click="closeDeleteDialog"
          >
            CANCEL
          </v-btn>
          <v-btn
            outlined
            :loading="delMirrorLoading"
            :disabled="delMirrorLoading"
            color="error"
            @click="confirmDeleteDialog"
          >
            DELETE
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="disableDialog"
      width="560"
    >
      <v-card :loading="disableMirrorLoading">
        <v-card-title class="body-1" primary-title>
          Disable Mirror
          <v-spacer />
          <v-btn icon :disabled="disableMirrorLoading" @click="closeDisableDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="py-6">
          <span class="body-1 grey--text text--darken-4">
            Mirror
            <span class="black--text font-weight-medium">{{ disableTarget?.name || '-' }}</span>
            will disable
          </span>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            :disabled="disableMirrorLoading"
            text
            @click="closeDisableDialog"
          >
            CANCEL
          </v-btn>
          <v-btn
            outlined
            :loading="disableMirrorLoading"
            :disabled="disableMirrorLoading"
            color="warning"
            @click="confirmDisableDialog"
          >
            DISABLE
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { API } from '@/utils/api'
  import LangRow from '@/components/core/SelectLang/LangRow/LangRow.vue'
  import { DrawerDialog } from '@/components/core/DrawerDialog'
  import mirrorMixin from '@/modules/mirrors-redirectors/mixins/mirror'
  import mirrorDialogsMixin from '@/modules/mirrors-redirectors/mixins/mirrorDialogs'
  import mirrorActionsMixin from '@/modules/mirrors-redirectors/mixins/mirrorActions'
  import MirrorGroupForm from './MirrorGroupForm.vue'
  import MirrorForm from '../Mirrors/MirrorForm.vue'
  import MirrorCommentForm from '../Mirrors/MirrorCommentForm.vue'
  import MirrorGeoListDialog from '../Mirrors/MirrorGeoListDialog.vue'
  import { TAG_LABELS } from '@/modules/feature-config/constants/const'

  const STATUS_INFO_ITEMS = [
    { key: 'new', title: 'New', description: 'Mng added the domain' },
    { key: 'ns_ready', title: 'NS Ready', description: 'System pulled up NS records' },
    { key: 'password_protected', title: 'Pass. protected', description: 'Domain is configurated and accessible with password' },
    { key: 'active', title: 'Active', description: 'The domain has been released to the world' },
    { key: 'error', title: 'Error', description: 'Error occured' },
    { key: 'deleted', title: 'Deleted', description: 'Mirror was removed' },
    { key: 'draft', title: 'Draft', description: 'In Progress, not active' },
    { key: 'maintenance', title: 'Maintenance', description: 'Maintenance mode' },
  ]

  export default {
    name: 'MirrorsGroupsTab',
    emits: ['show-audit-log'],
    mixins: [mirrorMixin, mirrorDialogsMixin, mirrorActionsMixin],
    components: { LangRow, DrawerDialog, MirrorGroupForm, MirrorForm, MirrorCommentForm, MirrorGeoListDialog },
    props: {
      project: {
        type: Number,
        default: null,
      },
      searchArgs: {
        type: Object,
        default: null,
      },
      canEdit: {
        type: Boolean,
        default: false,
      },
      canReadFeatureConfig: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      selectedByGroup: {},
      loading: false,
      mainDomainData: null,
      mirror_groups: [],
      redirectors: [],
      project_config: null,
      groupsPanel: [],
      groupFormOpen: false,
      groupSaveLoading: false,
      selectedGroup: null,
      mirrorSaveLoading: false,
      tagLabels: TAG_LABELS,

      statusFilterValues: STATUS_INFO_ITEMS.map((item) => item.key),

      filters: {
        mirror: { data: '', type: 'text' },
        status: { data: [], type: 'select' },
      },

      loadedId: null,

      tableHeaders: [
        { text: 'Mirror', value: 'mirror', sortable: true },
        { text: 'Default Geo Rule', value: 'defaultRule', sortable: false, align: 'center' },
        { text: 'Redirector Geo', value: 'redirectGeo', sortable: false, align: 'center' },
        { text: 'Allow Geo', value: 'allowGeo', sortable: false, align: 'center'},
        { text: 'Blocked Geo', value: 'blockedGeo', sortable: false, align: 'center'},
        { text: 'Banned Geo', value: 'bannedGeo', sortable: false, align: 'center' },
        { text: 'Target Geo', value: 'targetGeo', sortable: false, align: 'center' },
        { text: 'Popular Geo', value: 'popularGeo', sortable: false, align: 'center' },
        { text: 'Language Locale', value: 'locales', sortable: false, align: 'center' },
        { text: 'Status', value: 'status', sortable: false, align: 'center' },
        { text: 'Password', value: 'password', sortable: false, align: 'center' },
        { text: 'Comments', value: 'comments', sortable: false, align: 'center' },
        { text: 'Features', value: 'features', sortable: false, align: 'center' },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
    }),
    computed: {
      hasProject() {
        return this.project != null
      },
      selectedMirrorIds() {
        const unique = new Set()
        Object.values(this.selectedByGroup).forEach((rows) => {
          (rows || []).forEach((item) => {
            const id = item?.mirror_id
            if (id) unique.add(id)
          })
        })
        return [...unique]
      },
      sortedMirrorGroups() {
        return _.orderBy(this.mirror_groups, g => g?.name?.toLowerCase() === 'parked' ? 1 : 0)
      },
      allUniqueMirrors() {
        const map = new Map()
        for (const group of this.mirror_groups) {
          for (const mirror of (group.mirrors || [])) {
            if (!map.has(mirror.mirror_id)) {
              map.set(mirror.mirror_id, mirror)
            }
          }
        }
        return [...map.values()]
      },
      statusInfoItems() {
        return STATUS_INFO_ITEMS
      },
    },
    watch: {
      project: {
        immediate: true,
        handler(entityId) {
          if (entityId) {
            if (entityId === this.loadedId) {
              return
            }
            this.loadMirrorsGroups(entityId)
            return
          }
          this.loadedId = null
          this.mainDomainData = null
          this.mirror_groups = []
          this.redirectors = []
          this.project_config = null
        },
      },
    },
    methods: {
      groupRedirectorName(group_mirror_id) {
        return this.redirectors.find((r) => r.group_mirror_id === group_mirror_id)?.name ?? ''
      },
      matchesMirrorFilters(item) {
        const mirror = String(this.filters.mirror.data ?? '').trim().toLowerCase()
        const raw = this.filters.status.data
        const statuses = Array.isArray(raw) ? raw : (raw ? [raw] : [])

        const mirrorMatch = !mirror || String(item?.name ?? '').toLowerCase().includes(mirror)
        const statusMatch = !statuses.length || statuses.includes(String(item?.status ?? ''))

        return mirrorMatch && statusMatch
      },

      getSortedGroupMirrors(group) {
        const mirrors = group?.mirrors ?? [];
        const primaryId = group?.primary_mirror_id;

        if (!primaryId) return mirrors;

        return [
          ...mirrors.filter(m => m.mirror_id === primaryId),
          ...mirrors.filter(m => m.mirror_id !== primaryId),
        ];
      },

      getFilteredGroupMirrors(group) {
        return this.getSortedGroupMirrors(group).filter((item) => this.matchesMirrorFilters(item))
      },
      getPrimaryMirror(group) {
        return (group?.mirrors || []).find(
          (m) => m.mirror_id === group?.primary_mirror_id
        ) || null
      },
      tagLabel(tag) {
        return this.tagLabels[tag] || tag
      },

      async confirmDeleteDialog() {
        if (!this.deleteTarget?.mirror_id) return
        await this.deleteMirrors([this.deleteTarget.mirror_id])
      },
      async confirmDisableDialog() {
        if (!this.disableTarget?.mirror_id) return
        await this.disableMirrors([this.disableTarget.mirror_id])
      },

      getBulkReloadMethod() {
        return 'loadMirrorsGroups'
      },
      afterDeleteMirrors() {
        this.selectedByGroup = {}
      },
      afterDisableMirrors() {
        this.selectedByGroup = {}
      },

      async onBulkAction(action, label) {
        const ids = this.selectedMirrorIds
        if (!ids.length) return
        const yes = await this.$root.$confirm('Confirm', `${label} ${ids.length} selected mirrors?`)
        if (yes) await action(ids)
      },
      onDeleteSelected() {
        return this.onBulkAction(this.deleteMirrors, 'Delete')
      },
      onDisableSelected() {
        return this.onBulkAction(this.disableMirrors, 'Disable')
      },

      updateSelected(group_mirror_id, rows) {
        this.$set(this.selectedByGroup, group_mirror_id, rows)
      },

      openGroupForm(group) {
        this.selectedGroup = group || null
        this.groupFormOpen = true
      },
      closeGroupForm(close = false) {
        if (this.groupSaveLoading && !close) return
        this.groupFormOpen = false
        this.selectedGroup = null
      },

      async saveGroup({ group }) {
        if (!group || !this.canEdit) return

        try {
          this.groupSaveLoading = true
          await API.call('/$v/mirrors-redirectors/mirrors-groups/update.json', {
            entity_id: this.project,
            group,
          })
          this.$showSuccess('Saved successfully')
          await this.loadMirrorsGroups(this.project)
          this.closeGroupForm(true)
        } catch (e) {
          this.$showError(e?.response?.data?.message || 'Failed to save group')
        } finally {
          this.groupSaveLoading = false
        }
      },

      async saveMirror({ mirror }) {
        if (!mirror || !this.canEdit) return

        try {
          this.mirrorSaveLoading = true
          await API.call('/$v/mirrors-redirectors/mirrors/update.json', { mirror })
          this.$showSuccess('Saved successfully')
          await this.loadMirrorsGroups(this.project)
          this.closeMirrorForm(true)
          this.closeCommentForm(true)
        } catch (e) {
          this.$showError(e?.response?.data?.message || 'Failed to save mirror')
        } finally {
          this.mirrorSaveLoading = false
        }
      },
      async loadMirrorsGroups(entityId) {
        this.loading = true
        this.mainDomainData = null
        this.mirror_groups = []
        this.redirectors = []
        this.project_config = null
        this.selectedByGroup = {}
        try {
          const { project: searchProject, ...searchPayload } = this.searchArgs || {}
          const entity_id = searchProject ?? entityId ?? this.project

          const { data = {} } = await API.call('/$v/mirrors-redirectors/mirrors-groups/list.json', {
            entity_id,
            ...searchPayload,
          })
          this.loadedId = entity_id
          this.mainDomainData = data.main_domain ?? null
          this.mirror_groups = Array.isArray(data.mirror_groups) ? data.mirror_groups : []
          this.redirectors = Array.isArray(data.redirectors) ? data.redirectors : []
          this.project_config = data.project_config ?? null
        } catch (e) {
          this.$showError(e?.response?.data?.message || 'Failed to load mirrors groups')
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
  .mirrors-groups-tab {
    min-height: 100vh;
    display: flex;
    flex-direction: column;

    &__content {
      flex-grow: 1;
    }

    .expansion {
      display: flex;
      flex-direction: column;
      gap: 16px;

      &-panel {
        height: 56px;

        &__length {
            background: #828282;
            color: white;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            font-size: 8px;
            line-height: 18px;
            text-align: center;
          }

        &-header {
          height: 56px;
          padding: 12px 16px 10px 10px;

          &__col {
            min-height: 40px;
          }
        }

        &-content {
          ::v-deep .v-expansion-panel-content__wrap {
            padding: 0;
          }

          &__table {
            border: 1px solid #E0E0E0;
          }
        }
      }
    }

    &__footer {
      flex-shrink: 0;
      border-color: #E0E0E0;

      &-status-list {
        display: flex;
        flex-wrap: wrap;
        gap: 10px 18px;
      }

      &-status {
        display: flex;
        align-items: center;
        gap: 6px;

        &-chip {
          line-height: 1;
        }
      }
    }

    &__actions-menu {
      min-width: 168px;

      & ::v-deep .v-list-item__title {
        color: #000000DE;
        font-size: 12px;
        font-weight: 400;
      }

      &::v-deep .v-list-item__icon {
        margin-right: 0px !important;
      }

      &::v-deep .v-icon {
        color: #1C1B1F;
      }
    }

    &__pill-chip {
      height: 24px;
      min-height: 24px;
      padding: 5px 8px;
      border-radius: 50px;
      font-size: 12px;
    }

    &__pill-chip-lg {
      height: 32px;
      min-height: 32px;
      padding: 5px 12px;
      border-radius: 50px;
      font-size: 13px;
    }

    &__geo-cell {
      cursor: pointer;
      display: inline-flex;
      width: 100%;
      justify-content: center;

      &-items ::v-deep span {
        cursor: pointer;
      }
    }
  }

  .auto-width {
    width: auto !important;
  }

  .lang-row {
    justify-content: center;
    align-content: center;
  }

</style>
